import { Options, Vue } from 'vue-class-component';
import { Inject, Watch } from 'vue-property-decorator';
import type { LocationQueryValue } from 'vue-router';

import ConfigurationService from '@/services/configuration.service';
import ConfigError from '@/components/config/config-error.component.vue';
import ConfigLoading from '@/components/config/config-loading.component.vue';
import HeaderEntry from '@/components/header/header-entry.component.vue';
import TheFooter from '@/components/footer/footer.component.vue';
import { APP_THEME_SABLE, resolveAppTheme } from '../../common/src';
import router from './router';
import store from './store';
import { THEME_CONFIG } from './themeConfig';
import { JoinFlowPageEnum, joinFlowPageForPath } from './views/join/join-flow-registry';

@Options({
  components: {
    ConfigError,
    ConfigLoading,
    HeaderEntry,
    TheFooter,
  },
  computed: {
    showFooter() {
      return THEME_CONFIG[resolveAppTheme(store.state.config?.themecustomization.theme)].showFooter;
    },
  },
})
export default class App extends Vue {
  @Inject('configurationService')
  public configurationService!: ConfigurationService;

  // Keeps the header hidden after header=0 is removed from the URL.
  private headerHiddenRoute: string | undefined;

  get isSableTheme(): boolean {
    return resolveAppTheme(store.state.config?.themecustomization.theme) === APP_THEME_SABLE;
  }

  get isSableJoinFlow(): boolean {
    return this.isSableTheme && router.currentRoute.value.path.startsWith('/join');
  }

  get isSableProfile(): boolean {
    return this.isSableTheme && router.currentRoute.value.name === 'Profile';
  }

  get isSableEasyCheckout(): boolean {
    return this.isSableTheme && joinFlowPageForPath(router.currentRoute.value.path) === JoinFlowPageEnum.EasyCheckout;
  }

  get showHeader(): boolean {
    const currentRoute = router.currentRoute.value;
    if (this.headerHiddenRoute === currentRoute.fullPath) {
      return false;
    }

    const headerDisabled = this.isHeaderDisabled(currentRoute.query.header);

    if (headerDisabled) {
      return false;
    }

    return true;
  }

  @Watch('$route', { immediate: true })
  cleanHeaderQuery(): void {
    const currentRoute = router.currentRoute.value;
    const headerDisabled = this.isHeaderDisabled(currentRoute.query.header);

    if (!headerDisabled) {
      // There is nothing to remove when the current URL has no header=0 query value.
      if (this.headerHiddenRoute !== currentRoute.fullPath) {
        // The hidden marker belongs only to the route whose header query was removed. Clear it
        // after any subsequent navigation so a normal route can show its header again.
        this.headerHiddenRoute = undefined;
      }
      return;
    }

    // Remove only the header query so unrelated parameters, such as center and plan, survive.
    const query = { ...currentRoute.query };
    delete query.header;
    const cleanRoute = { path: currentRoute.path, query, hash: currentRoute.hash };

    // router.replace() triggers this watcher again without header=0. Remember the cleaned route
    // so that second invocation does not immediately make the header visible again.
    this.headerHiddenRoute = router.resolve(cleanRoute).fullPath;
    void router.replace(cleanRoute);
  }

  private isHeaderDisabled(headerQuery: LocationQueryValue | LocationQueryValue[] | undefined): boolean {
    return Array.isArray(headerQuery) ? headerQuery.includes('0') : headerQuery === '0';
  }

  get isSableAuthPage(): boolean {
    return this.isSableTheme && ['Login', 'ForgotPassword', 'ResetPassword'].includes(String(router.currentRoute.value.name));
  }

  get usesSableViewportShell(): boolean {
    return this.isSableJoinFlow || this.isSableAuthPage || this.isSableEasyCheckout;
  }

  beforeMount(): void {
    // config is loaded when the router changes, because we might to reload the config if for example we have an sso tocken
    // otherwise the same config is used
    // this.configurationService.loadConfiguration().catch(() => {});
  }
}
